import { useEffect, useRef, useState } from "react";
import { getAdsConfig } from "./ads";

declare global {
  interface Window { adsbygoogle?: unknown[]; }
}

let adScriptPromise: Promise<void> | null = null;

function loadAdSense(clientId: string): Promise<void> {
  if (adScriptPromise) return adScriptPromise;
  adScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(clientId)}`;
    script.crossOrigin = "anonymous";
    const nonce = document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')?.content;
    if (nonce && nonce !== "__SUREVAI_CSP_NONCE__") script.nonce = nonce;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      adScriptPromise = null;
      reject(new Error("AdSense did not load."));
    };
    document.head.appendChild(script);
  });
  return adScriptPromise;
}

export function AdPlacement({ placement = "content" }: { placement?: "content" | "analysis" | "text" }) {
  const config = getAdsConfig();
  const containerRef = useRef<HTMLElement | null>(null);
  const adRef = useRef<HTMLModElement | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (config.mode !== "production" || !config.clientId || !config.slot || !containerRef.current) return;
    let active = true;
    let requested = false;
    const ad = adRef.current;
    const statusObserver = ad && "MutationObserver" in window ? new MutationObserver(() => {
      if (ad.dataset.adStatus === "unfilled") setHidden(true);
    }) : null;
    if (ad && statusObserver) statusObserver.observe(ad, { attributes: true, attributeFilter: ["data-ad-status"] });
    const requestAd = () => {
      if (requested) return;
      requested = true;
      void loadAdSense(config.clientId!).then(() => {
        const ad = adRef.current;
        if (!active || !ad || ad.dataset.adsbygoogleStatus) return;
        try { (window.adsbygoogle = window.adsbygoogle || []).push({}); }
        catch { setHidden(true); }
      }).catch(() => { if (active) setHidden(true); });
    };
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          requestAd();
        }
      }, { rootMargin: "600px 0px" });
      observer.observe(containerRef.current);
      return () => { active = false; observer.disconnect(); statusObserver?.disconnect(); };
    }
    requestAd();
    return () => { active = false; statusObserver?.disconnect(); };
  }, [config.mode, config.clientId, config.slot]);

  if (config.mode === "off" || hidden) return null;
  return <section ref={containerRef} className={`ad-section ad-section--${placement}`} aria-label="Advertisement" data-share-hide={placement === "analysis" ? "" : undefined}>
    <div className="ad-section-inner">
      <span className="ad-label">Advertisement</span>
      {config.mode === "test"
        ? <div className="ad-test" role="img" aria-label="Test advertisement placement">Test advertisement placement</div>
        : <ins ref={adRef} className="adsbygoogle" style={{ display: "block" }} data-ad-client={config.clientId!} data-ad-slot={config.slot!} data-ad-format="auto" data-full-width-responsive="true" />}
    </div>
  </section>;
}
